import { get, size } from 'lodash';
import Moment from 'moment';
import serialize from 'serialize-javascript';
import keyMirror from 'keymirror';
import { PhoneNumberFormat, PhoneNumberUtil } from 'google-libphonenumber/dist/libphonenumber';

import { getHostCMS } from '../../api-wrapper/api-config';
import safeLocalStorage from './safe-local-storage';

import { MANAGE_BOOKING_URLS } from '../constants/urls';
import AIRCRAFT_NAME_POSITION from '../constants/aircraft-name-position';
import AIRCRAFT_NAMES from '../constants/aircraft-names';
import { PREMIER } from '../constants/premier-types';

import SeatMixedPremier from '../../images/fare-families/seatPremierMixed.png';
import SeatPremierLight from '../../images/fare-families/seatPremierLight.png';
import SeatPremier from '../../images/fare-families/seatPremier.png';
import SeatPremierOne from '../../images/fare-families/seatPremierOne_787-8.png';
import SeatPremierOne789 from '../../images/fare-families/seatPremierOne_787-9.png';

const phoneUtil = PhoneNumberUtil.getInstance();

function isPnrOrEticket(code) {
  // Validate if it's a PNR (6 alphanumeric characters)
  if (typeof code === 'string' && code.length === 6 && /^[A-Za-z0-9]+$/.test(code)) {
    return 'pnr';
  }

  // Validate if it's an eTicket (13 digits)
  if (typeof code === 'string' && code.length === 13 && /^\d{13}$/.test(code)) {
    return 'eticket';
  }

  // If it's neither
  return null;
}

function getCabinImageURL(ancillaryMybDescriptions, position) {
  let cabinImage = '';
  if (ancillaryMybDescriptions) {
    const {
      UPG,
    } = ancillaryMybDescriptions;

    if (UPG) {
      const {
        TotemImageModal,
        TotemImageModal2,
        TotemImageModal3,
        TotemImageModal4,
        TotemImageModal5,
      } = UPG;

      switch (position) {
        case 1:
          cabinImage = TotemImageModal; // 787-9
          break;
        case 2:
          cabinImage = TotemImageModal5; // 787-8
          break;
        case 3:
          cabinImage = TotemImageModal4; // 737-800
          break;
        case 4:
          cabinImage = TotemImageModal3; // 737
          break;
        case 5:
          cabinImage = TotemImageModal2; // E90
          break;
        default:
          // Agregar imagen default;
          cabinImage = null;
          break;
      }
    }
  }

  return cabinImage;
}
function sortAlphabetically(arrival, departure) {
  if (arrival < departure) {
    return `${arrival}${departure}`;
  }
  return `${departure}${arrival}`;
}

function getTimeFormatCopyID(hours, minutes) {
  if (hours !== 0 && minutes !== 0) {
    return 'TIME_HOURS_MINUTES';
  } else if (hours !== 0 && minutes === 0) {
    return 'TIME_HOURS';
  } else if (hours === 0 && minutes !== 0) {
    return 'TIME_MINUTES';
  } else if (hours === 0 && minutes === 0) {
    console.warn('Both hours and minutes are zero'); // eslint-disable-line no-console
    return '';
  }
}

function isIrops() {
  return window.location.href?.includes('isIrops=true');
}

function getFlightTypeCopyIDByLeg(leg) {
  let copyIDFlightType = null;
  switch (leg.legType) {
    case 'NON-STOP':
      copyIDFlightType = 'APP_NONSTOP';
      break;
    case 'CONNECTING':
      copyIDFlightType = 'APP_CONNECTING';
      break;
    case 'THRU':
      copyIDFlightType = 'APP_THRU';
      break;
  }
  return copyIDFlightType;
}

function getNumberOfDaysCopyID(numberOfDays) {
  if (numberOfDays === 1) {
    return 'APP_NEXT_DAY';
  } else if (numberOfDays === -1) {
    return 'APP_PREVIOUS_DAY';
  } else if (numberOfDays >= 2 || numberOfDays < -1) {
    return 'APP_NUM_DAYS';
  }
}

function getFareAndCabin(allSegments) {
  const fareFamily = [];
  const fareAndCabinName = [];

  for (let i = 0; i < allSegments.length; i++) {
    fareAndCabinName[i] = allSegments[i].fareAndCabinName;
    fareFamily[i] = allSegments[i].fareFamily;
    if (i - 1 > -1) {
      if (fareFamily[i] !== fareFamily[i - 1]) {
        return false;
      }
    }
  }

  if (fareAndCabinName && fareAndCabinName[0] && fareAndCabinName[0].includes('FLEXIBLE') && fareFamily[0] !== 'FLEXIBLE') {
    fareFamily.push(fareAndCabinName[0]);
  } else {
    switch (fareFamily[0]) {
      case 'BASIC': fareFamily.push('BASIC_FARE_TOURIST_CABIN'); break;
      case 'ECONOMIC': fareFamily.push('ECONOMIC_FARE_TOURIST_CABIN'); break;
      case 'CLASSIC': fareFamily.push('CLASSIC_FARE_TOURIST_CABIN'); break;
      case 'FLEXIBLE': fareFamily.push('FLEXIBLE_FARE_TOURIST_CABIN'); break;
      case 'AMPLUS': fareFamily.push('AMPLUS_FARE_TOURIST_CABIN'); break;
      case 'COMFORT': fareFamily.push('COMFORT_FARE_PREMIER_CABIN'); break;
      case 'PREMIER': fareFamily.push('PREMIER_FARE_PREMIER_CABIN'); break;
      default: fareFamily.push('');
    }
  }

  return fareFamily;
}

function getFareAndCabinName(allSegments) {
  const fareFamily = [];
  allSegments.forEach(segment => {
    if (segment && segment.fareAndCabinName && (segment.fareAndCabinName.includes('FLEXIBLE') || segment.fareAndCabinName.includes('FLEX')) && segment.fareAndCabinName !== 'FLEXIBLE') {
      let cabinName = segment.fareAndCabinName;
      if (segment.fareFamily === 'PREMIER') {
        cabinName = 'PREMIER_FLEXIBLE_FARE_PREMIER_CABIN';

        if (segment.isPremierOne) {
          cabinName = 'CC_PREMIER_ONE_FLEX_FARE_PREMIER_CABIN';
        } else if (segment.isPremierLight) {
          cabinName = 'PREMIER_FLEXIBLE_LIGHT_FARE_PREMIER_CABIN';
        }
      }

      fareFamily.push(cabinName);
    } else if (segment.fareAndCabinName === 'CLASSICPP_FARE_TOURIST_CABIN' || segment.fareAndCabinName === 'PREMIERPP_FARE_PREMIER_CABIN') {
      let cabinName = '';
      if (segment.fareAndCabinName === 'CLASSICPP_FARE_TOURIST_CABIN') {
        cabinName = 'MYB_AWRTKT__FFARE__CLASSIC_TICKET';
      } else if (segment.fareAndCabinName === 'PREMIERPP_FARE_PREMIER_CABIN') {
        cabinName = 'MYB_AWRTKT__FFARE__PREMIER_TICKET';
      }

      fareFamily.push(cabinName);
    } else {
      switch (segment.fareFamily) {
        case 'BASIC': fareFamily.push('BASIC_FARE_TOURIST_CABIN'); break;
        case 'ECONOMIC': fareFamily.push('ECONOMIC_FARE_TOURIST_CABIN'); break;
        case 'CLASSIC': fareFamily.push('CLASSIC_FARE_TOURIST_CABIN'); break;
        case 'FLEXIBLE': fareFamily.push('FLEXIBLE_FARE_TOURIST_CABIN'); break;
        case 'AMPLUS': fareFamily.push('AMPLUS_FARE_TOURIST_CABIN'); break;
        case 'COMFORT': fareFamily.push('COMFORT_FARE_PREMIER_CABIN'); break;
        case 'PREMIER':
          let cabinName = 'PREMIER_FARE_PREMIER_CABIN';

          if (segment.isPremierOne) {
            cabinName = 'PREMIER_ONE_FARE_PREMIER_CABIN';
          } else if (segment.isPremierLight) {
            cabinName = 'PREMIER_LIGHT_FARE_PREMIER_CABIN';
          }
          fareFamily.push(cabinName);
          break;
        case 'CLASICA_CORPORATIVA': fareFamily.push(segment.fareAndCabinName); break;
        case 'MAIN_LIGHT': fareFamily.push('MAIN LIGHT'); break;
        default: fareFamily.push('');
      }
    }
  });

  return fareFamily;
}

function getFareFamilyName(allSegments) {
  const fareFamily = [];
  // eslint-disable-next-line no-unused-expressions
  allSegments?.forEach(segment => {
    if (segment?.fareAndCabinName && (segment?.fareAndCabinName?.includes('FLEXIBLE') || segment?.fareAndCabinName?.includes('FLEX')) && segment?.fareAndCabinName !== 'FLEXIBLE') {
      let cabinName = segment?.fareAndCabinName;

      if (segment?.fareFamily === 'PREMIER') {
        cabinName = 'CC_PREMIER_FLEXIBLE_FARE_PREMIER_CABIN';

        if (segment?.isPremierOne) {
          cabinName = 'CC_PREMIER_ONE_FLEX_FARE_PREMIER_CABIN';
        } else if (segment?.isPremierLight) {
          cabinName = 'CC_PREMIER_LIGHT_FARE_PREMIER_CABIN';
        }
      }

      fareFamily.push(cabinName);
    } else if (segment?.fareAndCabinName === 'CLASSICPP_FARE_TOURIST_CABIN' || segment?.fareAndCabinName === 'PREMIERPP_FARE_PREMIER_CABIN') {
      let cabinName = '';
      if (segment?.fareAndCabinName === 'CLASSICPP_FARE_TOURIST_CABIN') {
        cabinName = 'CC_CLASSICPP_FARE_TOURIST_CABIN';
      } else if (segment?.fareAndCabinName === 'PREMIERPP_FARE_PREMIER_CABIN') {
        cabinName = 'CC_PREMIERPP_FARE_PREMIER_CABIN';
      }

      fareFamily.push(cabinName);
    } else {
      switch (segment?.fareFamily) {
        case 'BASIC': fareFamily.push('CC_BASIC_FARE_TOURIST_CABIN'); break;
        case 'ECONOMIC': fareFamily.push('CC_ECONOMIC_FARE_TOURIST_CABIN'); break;
        case 'CLASSIC': fareFamily.push('CC_CLASSIC_FARE_TOURIST_CABIN'); break;
        case 'FLEXIBLE': fareFamily.push('CC_CLASSIC_FLEXIBLE_FARE_TOURIST_CABIN'); break;
        case 'AMPLUS': fareFamily.push('CC_AMPLUS_FARE_TOURIST_CABIN'); break;
        case 'COMFORT': fareFamily.push('CC_COMFORT_FARE_PREMIER_CABIN'); break;
        case 'PREMIER':
          let cabinName = 'CC_PREMIER_FARE_PREMIER_CABIN';

          if (segment?.isPremierOne) {
            cabinName = 'CC_PREMIER_ONE_FARE_PREMIER_CABIN';
          } else if (segment?.isPremierLight) {
            cabinName = 'CC_PREMIER_LIGHT_FARE_PREMIER_CABIN';
          }
          fareFamily.push(cabinName);
          break;
        default: fareFamily.push('');
      }
    }
  });

  return fareFamily;
}

function getBookingClassForBoletoPremio(allSegments) {
  const bookingClass = allSegments.map(segment => {
    if (segment && segment.bookingClass) {
      return segment.bookingClass;
    } else {
      return '';
    }
  });

  return bookingClass;
}

function getHighestCabinAircraft(cabinAircraftsArray, ancillaryMybDescriptions, upgradeInfo) {
  let highestCabinAircraft = null;

  if (cabinAircraftsArray && cabinAircraftsArray.length > 0) {
    const cabinPositions = [];

    cabinAircraftsArray.forEach(cabinAircraft => {
      if (AIRCRAFT_NAME_POSITION && AIRCRAFT_NAMES[cabinAircraft] && AIRCRAFT_NAME_POSITION[AIRCRAFT_NAMES[cabinAircraft]]) {
        cabinPositions.push(AIRCRAFT_NAME_POSITION[AIRCRAFT_NAMES[cabinAircraft]]);
      }
    });

    if (cabinPositions.length > 0) {
      const orderedCabins = cabinPositions.sort((a, b) => a - b);

      highestCabinAircraft = getCabinImageURL(ancillaryMybDescriptions, orderedCabins[0]);
    }
  }

  if (!highestCabinAircraft && upgradeInfo) {
    if (upgradeInfo.upgradeType === PREMIER.PREMIER_MIXED) {
      upgradeInfo.upgradeType = PREMIER.PREMIER;
    }

    highestCabinAircraft = getBoxImageFarePremierLocalURL(upgradeInfo);
  }

  return highestCabinAircraft;
}

function getBoxImageFarePremierLocalURL(upgradeInfo) {
  const response = {
    src: '',
    alt: '',
  };
  const {
    cabinAircrafts,
    upgradeType,
  } = upgradeInfo;

  switch (upgradeType) {
    case PREMIER.PREMIER:
      response.src = SeatPremier;
      response.alt = 'Premier';
      break;
    case PREMIER.PREMIER_MIXED:
      response.src = SeatMixedPremier;
      response.alt = 'Premier';
      break;
    case PREMIER.ONE:
    case PREMIER.ONE_MIXED:
      if (cabinAircrafts.some((item) => item === '789')) {
        response.src = SeatPremierOne789;
      } else {
        response.src = SeatPremierOne;
      }
      response.alt = 'PremierOne';
      break;
    case PREMIER.LIGHT:
      response.src = SeatPremierLight;
      response.alt = 'PremierLight';
      break;
  }

  return response;
}

/**
 * @param {string} time
 * @returns if storefront is us, return time with format a or p
 */
function getTimeFormat(time, storefront) {
  return storefront === 'us' ? time.replace(/ /g, '').slice(0, -1) : time;
}

function passengerCount(travelerCollection) {
  let adultMinors = 0;
  let adults = 0;
  let child = 0;
  let infants = 0;
  if (travelerCollection) {
    travelerCollection.forEach(traveler => {
      if (traveler?.paxType === 'ADULT') {
        const dateOfBirth = Moment(traveler?.dateOfBirth).format('YYYYMMDD');
        const currentAge = Moment().diff(Moment(dateOfBirth, 'YYYYMMDD'), 'years');
        if (currentAge >= 15 && currentAge < 18) {
          adultMinors = adultMinors + 1;
        } else {
          adults = adults + 1;
          if (traveler?.infant) {
            infants = infants + 1;
          }
        }
      } else if (traveler?.paxType === 'CHILD') {
        child = child + 1;
      }
    });
  }

  const data = {
    'adults': adults,
    'child': child,
    'infants': infants,
    'adultMinors': adultMinors,
  };
  return data;
}

function luggageAncillary(travelerCollection, products) {
  const matchedItems = [];
  const uniqueItems = [];
  let checkItems = '';
  const seen = new Map();
  const seen1 = new Map();
  const selectedBags = [];

  travelerCollection.forEach((passengers) => {
    const includedBaggage = passengers?.freeBaggageAllowancePerLegsArray || [];
    includedBaggage.forEach((baggage) => {
      const checkedWeights = baggage?.freeBaggageAllowance?.checkedWeightCollection[0] || [];
      let iterate = baggage?.freeBaggageAllowance?.checked;
      iterate = (Boolean(passengers?.infant) && iterate > 1) ? iterate - 1 : iterate;
      Array.from({ length: iterate }).forEach((_, ind) => {
        selectedBags.push({ route: baggage?.legCode, quantity: 1, count: ind, checkedFreeWeight: checkedWeights?.checkedFreeWeight });
      });
    });

    const passengerData = passengers?.ancillaries?.ancillaries || [];
    passengerData.forEach((ancillaries) => {
      if ((ancillaries?.commercialName || ancillaries?.ancillary?.commercialName).includes('BAG')) {
        selectedBags.push(ancillaries);
      }
    });
  });

  selectedBags.forEach(item1 => {
    if (item1?.checkedFreeWeight) {
      const checkKey = JSON.stringify({ checkedFreeWeight: item1?.checkedFreeWeight, route: item1?.route, count: item1?.count });
      if (seen1.has(checkKey)) {
        seen1.get(checkKey).quantity++;
      } else {
        const newItem = { ...item1 };
        uniqueItems.push(newItem);
        seen1.set(checkKey, newItem);
      }
    } else {
      const key = JSON.stringify({ commercialName: item1?.ancillary?.commercialName || item1?.commercialName, segmentCodeAux: item1?.segmentCodeAux, legCode: item1?.legCode });
      if (seen.has(key)) {
        seen.get(key).quantity += item1?.quantity;
      } else {
        const newItem = { ...item1 };
        uniqueItems.push(newItem);
        seen.set(key, newItem);
      }
    }
  });
  const routeCounters = {};
  uniqueItems.map(item1 => {
    const match = products.find(item2 => (item1?.type || item1?.ancillary?.type) === item2?.id);
    if (match) {
      if (item1?.ancillary?.redeemedCP) {
        match.price = 0;
        match.pricepoints = item1?.ancillary?.currency?.totalCPPoints;
      } else {
        match.price = item1?.price || item1?.ancillary?.currency?.total;
        match.pricepoints = 0;
      }
      matchedItems.push({ ...match, route: item1?.legCode, quantity: item1?.quantity, isPaid: item1?.isPaid });
    } else {
      if (item1?.checkedFreeWeight) {
        const { route, quantity, checkedFreeWeight } = item1;
        if (!routeCounters[route]) {
          routeCounters[route] = 1;
        } else {
          routeCounters[route]++;
        }

        const suffixes = ['th', 'st', 'nd', 'rd'];
        const lastDigit = routeCounters[route] % 10;
        const suffix = lastDigit <= 3 ? suffixes?.[lastDigit] : suffixes?.[0];

        let bagUnitValue = '';
        if (checkedFreeWeight.toString() === '32' || checkedFreeWeight.toString() === '70.5') {
          bagUnitValue = '32kg';
        } else if (checkedFreeWeight.toString() === '25' || checkedFreeWeight.toString() === '55') {
          bagUnitValue = '25kg';
        } else if (checkedFreeWeight.toString() === '45' || checkedFreeWeight.toString() === '99') {
          bagUnitValue = '45kg';
        }

        checkItems = {
          brand: 'Aeromexico',
          id: 'included',
          name: `${routeCounters?.[route]}${suffix} bag ${bagUnitValue}`,
          price: 0,
          quantity: quantity,
          route: route,
        };
        matchedItems.push(checkItems);
      }
    }
  });
  return matchedItems;
}

function emptyBag() {
  const params = {
    'item_name': 'NA',
    'coupon': 'NA',
    'discount': 0,
    'item_brand': 'NA',
    'item_category': 'Ancillary',
    'item_category2': 'Luggage',
    'item_category3': 'NA',
    'item_list_name': 'NA',
    'item_affiliation': 'NA',
    'price': 0,
    'quantity': 0,
    'index': 0,
    'market_ndod_item': 'NA',
    'market_od_item': 'NA',
    'market_segment_item': 'NA',
    'zone_item': 'NA',
    'region_item': 'NA',
    'flight_time_dep': 'NA',
    'flight_time': 'NA',
    'branded_fare': 'NA',
    'outbound_departure_date_item': 'NA',
    'inbound_departure_date_item': 'NA',
    'fop_type_item': 'NA',
    'price_points': 0,
    'airport_tax': 0,
    'aircraft_type': 'NA',
    'aircraft_subfleet': 'NA',
    'ap_item': 0,
    'ms_item': 0,
    'cabin_item': 'NA',
    'op_carrier': 'NA',
    'class_item': 'NA',
  };
  return params;
}

function bagNames(baggae) {
  const ordinalMap = {
    'First': '1st',
    'Second': '2nd',
    'Third': '3rd',
    'Fourth': '4th',
    'Fifth': '5th',
    'Sixth': '6th',
    'Seventh': '7th',
  };

  const [ordinalWord, , weight, unit] = baggae?.split(' ');
  const ordinalNumber = ordinalMap[ordinalWord];

  if (ordinalNumber) {
    return `${ordinalNumber} bag ${weight}${unit}`;
  } else {
    return baggae;
  }
}

function upgradeAncillary(pnrModel, upsellOffers) {
  const allSegmentCodes = pnrModel?.getSegmentCodesForAllSegments();
  const availableSegments = [];
  const upgradeAncillaries = [];

  (allSegmentCodes || []).forEach(segment => {
    const segmentByCode = pnrModel?.getSegmentByCode(segment);
    const ancillaryUpgrade = upsellOffers.find(upg => upg?.segmentCode === segment);

    if (ancillaryUpgrade?.upgradeAvailable === true && segmentByCode?.bookingClass !== 'F') {
      availableSegments.push(segmentByCode);
    }
  });

  availableSegments.forEach((segment) => {
    const ancillaryUpgrade = upsellOffers.find(upg => upg?.segmentCode === segment?.segmentCode);
    const route = segment?.departureAirport + '-' + segment?.arrivalAirport;
    ancillaryUpgrade.route = route;
    upgradeAncillaries.push(ancillaryUpgrade);
  });
  return upgradeAncillaries;
}

function getRandomKey() {
  // returns random, 4-digit, alphanumeric key, e.g. 'b1e9'
  return Math.floor((1 + Math.random()) * 0x10000).toString(16).substring(1);
}

function getFormattedNumber(value, selectedCountry) {
  if (value && value.phoneNumber && value.phoneNumber.length && selectedCountry) {
    const num = value.phoneNumber;
    let formattedNum = num;

    try {
      const isoCode = selectedCountry.value;
      const number = num.replace(/\D+/g, '');
      const phoneNumber = phoneUtil.parse(number, isoCode);

      formattedNum = phoneUtil.format(phoneNumber, PhoneNumberFormat.INTERNATIONAL);
    } catch (e) {
      console.log(e); // eslint-disable-line no-console
    }

    return formattedNum;
  } else {
    return null;
  }
}

function getFormattedNumberNoCountryCode(value, selectedCountry) {
  if (value?.phoneNumber?.length && selectedCountry) {
    const num = value.phoneNumber;
    let formattedNum = num;

    try {
      const isoCode = selectedCountry.value;
      const number = num.replace(/\D+/g, '');
      const phoneNumber = phoneUtil.parse(number, isoCode);

      formattedNum = phoneUtil.format(phoneNumber);
    } catch (e) {
      console.log(e); // eslint-disable-line no-console
    }

    return formattedNum;
  } else {
    return null;
  }
}
/**
 * Return HTML code from pre-HTML as:
 * [strong], [paragraph], [head1], [head2], [head3], [head4], [head5], [break]
 * @param {String} cadena pre-HTML
 * @return {String} HTML code
 */
function preHTML(cadena) {
  const pseudo = ['[strong]', '[paragraph]', '[head1]', '[head2]', '[head3]', '[head4]', '[head5]', '[break]', '[/strong]', '[/paragraph]', '[/head1]', '[/head2]', '[/head3]', '[/head4]', '[/head5]'];
  const stHTML = ['<strong>', '<p>', '<h1>', '<h2>', '<h3>', '<h4>', '<h5>', '<br />', '</strong>', '</p>', '</h1>', '</h2>', '</h3>', '</h4>', '</h5>'];
  if (cadena.length > 0) {
    for (const i in pseudo) {
      cadena = cadena?.split(pseudo[i])?.join(stHTML[i]);
    }
  }
  return cadena;
}

function getSegmentCodesUpsellOffers(ancillaryUpsellOffers) {
  const segmentCodesUpsellOffers = [];

  for (const element of ancillaryUpsellOffers) {
    if (element.upgradeAvailable) {
      segmentCodesUpsellOffers.push(element.segmentCode);
    }
  }

  return { segmentCodesUpsellOffers };
}

/*
 * @param {Shape.storefrontConfig} storefrontConfig - options for different storefronts
 * @param {String} language - current language
 * @param {String} storefront - current storefront
 *
 * @return {Array}
 */
function getStorefrontDropdownOptions(storefrontConfig) {
  const dropdownOptions = [];
  const topStorefrontOptions = [];

  Object.keys(storefrontConfig).forEach((storefrontCode) => {
    const dataForCurrentStorefront = storefrontConfig[storefrontCode];

    Object.keys(dataForCurrentStorefront).forEach(languageCode => {
      const dataForCurrentLanguage = dataForCurrentStorefront[languageCode];

      const optionName = `${dataForCurrentLanguage.name} - ${dataForCurrentLanguage.languageName}`;
      const storefrontIdentifier = dataForCurrentLanguage.identifier;
      const optionValues = {
        name: optionName,
        value: storefrontIdentifier,
      };

      switch (storefrontIdentifier) {
        case 'es-mx':
          return topStorefrontOptions[0] = optionValues;
        case 'en-us':
          return topStorefrontOptions[1] = optionValues;
        case 'es-us':
          return topStorefrontOptions[2] = optionValues;
        default:
          return dropdownOptions.push(optionValues);
      }
    });
  });

  const sortedOptions = sortArrayByProperty(dropdownOptions, 'name');
  const allOptions = topStorefrontOptions.concat(sortedOptions);

  return allOptions;
}

function hasSomeSeatSelectedBySegment(selectionPassengers, index) {
  const hasSeatSelectedBySegment = Object.keys(selectionPassengers).some(indexSegment => {
    if (parseInt(indexSegment, 10) === index) {
      return Object.keys(selectionPassengers[indexSegment]).some(
        indexPassenger => {
          return selectionPassengers[indexSegment][indexPassenger].choice;
        }
      );
    }
  });

  return hasSeatSelectedBySegment;
}

/**
 * Método que regresa información al momento de cargar el loading de nubes
 *
 * @param {Shape.storefrontConfig} storefrontConfig - options for different storefronts
 * @param {String} language - current language
 * @param {String} storefront - current storefront
 *
 * @return {Array}
*/
function getStorefrontIntertitialValues(storefrontConfig, language, storefront) {
  let intertitialObj = null;

  Object.keys(storefrontConfig).forEach((storefrontCode) => {
    const dataForCurrentStorefront = storefrontConfig[storefrontCode];

    Object.keys(dataForCurrentStorefront).forEach(languageCode => {
      const dataForCurrentLanguage = dataForCurrentStorefront[languageCode];
      const optionName = `${dataForCurrentLanguage.languageCode}-${dataForCurrentLanguage.code}`;
      const currentOptionName = `${language}-${storefront}`;

      if (optionName === currentOptionName) {
        if (dataForCurrentLanguage.interstitials) {
          intertitialObj = dataForCurrentLanguage.interstitials;
        }
      }
    });
  });
  return intertitialObj;
}

function getStorefrontSeatMapDiscountValues(storefrontConfig, language, storefront) {
  let seatMapDiscount = null;

  Object.keys(storefrontConfig).forEach((storefrontCode) => {
    const dataForCurrentStorefront = storefrontConfig[storefrontCode];

    Object.keys(dataForCurrentStorefront).forEach(languageCode => {
      const dataForCurrentLanguage = dataForCurrentStorefront[languageCode];
      const optionName = `${dataForCurrentLanguage.languageCode}-${dataForCurrentLanguage.code}`;
      const currentOptionName = `${language}-${storefront}`;

      if (optionName === currentOptionName) {
        if (dataForCurrentLanguage.seatdiscount) {
          seatMapDiscount = dataForCurrentLanguage.seatdiscount;
        }
      }
    });
  });
  return seatMapDiscount;
}

/*
 * @param {Shape.storefrontConfig} storefrontConfig - options for different storefronts
 * @param {String} language - current language
 * @param {String} storefront - current storefront
 *
 * @return {Array}
 */
function getStorefrontDropdownOptionsExcept(storefrontConfig) {
  const dropdownOptions = [];
  const topStorefrontOptions = [];

  Object.keys(storefrontConfig).forEach((storefrontCode) => {
    const dataForCurrentStorefront = storefrontConfig[storefrontCode];
    if (storefrontCode !== 'ar' && storefrontCode !== 'br' && storefrontCode !== 'ca' && storefrontCode !== 'cn' && storefrontCode !== 'jp' && storefrontCode !== 'kr') {
      Object.keys(dataForCurrentStorefront).forEach(languageCode => {
        const dataForCurrentLanguage = dataForCurrentStorefront[languageCode];

        const optionName = `${dataForCurrentLanguage.name} - ${dataForCurrentLanguage.languageName}`;
        const storefrontIdentifier = dataForCurrentLanguage.identifier;
        const optionValues = {
          name: optionName,
          value: storefrontIdentifier,
        };

        switch (storefrontIdentifier) {
          case 'es-mx':
            return topStorefrontOptions[0] = optionValues;
          case 'en-us':
            return topStorefrontOptions[1] = optionValues;
          case 'es-us':
            return topStorefrontOptions[2] = optionValues;
          default:
            return dropdownOptions.push(optionValues);
        }
      });
    }
  });

  const sortedOptions = sortArrayByProperty(dropdownOptions, 'name');
  const allOptions = topStorefrontOptions.concat(sortedOptions);

  return allOptions;
}

/**
 * @param {Array} options - Array of location options
 * @param {Array} storefront - Current selected storefront
 */
function getDefaultOption(options, storefront, language) {
  for (let i = 0; i < options.length; i++) {
    const option = options[i]?.value?.split('-');

    if (option[0] === language && option[1] === storefront) {
      return i;
    }
  }
}

function getDefaultOptionAirport(options, favorite) {
  for (let i = 0; i < options.lenth; i++) {
    if (options.name === favorite) {
      return options[i].name;
    }
  }
}

function openUrlInNewWindow(url, width = 390, height = 560) {
  if (url) {
    window.open(url, 'window', `width=${width},height=${height},`);
  }
}

function openUrlInNewTab(url) {
  if (url) {
    window.open(url, '_blank');
  }
}

function openCurrentWebsiteManageBooking(storefront, language) {
  const url = MANAGE_BOOKING_URLS[storefront][language];

  window.open(url, '_blank');
}

function sortArrayByProperty(array, property) {
  return array.sort((a, b) => {
    let propertyA = a[property];
    let propertyB = b[property];

    if (typeof (propertyA) === 'string' && typeof (propertyB) === 'string') {
      propertyA = propertyA.toLowerCase();
      propertyB = propertyB.toLowerCase();
    }

    if (propertyA < propertyB) {
      return -1;
    } else if (propertyA > propertyB) {
      return 1;
    }
    return 0;
  });
}

function isJsonObject(strData) {
  try {
    JSON.parse(strData);
  } catch (e) {
    return false;
  }
  return true;
}

function isSameArrays(a, b) {
  if (a.length !== b.length) {
    return false;
  }

  let sameArrays = true;
  for (let i = 0; i < a.length; i++) {
    if (!isSameObjects(a[i], b[i])) {
      sameArrays = false;
    }
  }

  return sameArrays;
}

function isSameObjects(x, y) {
  let objectsAreSame = true;
  for (const propertyName in x) {
    if (x[propertyName] !== y[propertyName]) {
      objectsAreSame = false;
      break;
    }
  }
  return objectsAreSame;
}

function isValidAgeForInsurance(ageInMonths, storefront) {
  if (storefront && (storefront === 'en-us' || storefront === 'es-us')) {
    return ageInMonths >= 24;
  } else {
    return ageInMonths >= 24 && ageInMonths < 1080;
  }
}

function buildQueryString(params) {
  const queryString = [];

  for (const property in params) {
    if (params.hasOwnProperty(property)) {
      queryString.push(encodeURIComponent(property) + '=' + encodeURIComponent(params[property]));
    }
  }

  return queryString.join('&');
}
/**
 * dynamic ajax request
 * @param data  Object sent in the request
 * @param url  URL of the request
 * @param method  request method
 * @param headers  request headers array
 * @returns {Promise}
 */
function sendHttpRequest(data, url, method, headers, contactUsForms) {
  return new Promise(
    function(resolve, reject) {
      const xmlhttp = new XMLHttpRequest();
      xmlhttp.onreadystatechange = function() {
        if (xmlhttp.readyState === 4) {
          if (xmlhttp.status === 200) {
            resolve(this.response);
          } else {
            reject(new Error(this.statusText));
          }
        }
      };
      xmlhttp.onerror = function() {
        reject(new Error(
          'XMLHttpRequest Error: ' + this.statusText));
      };
      xmlhttp.open(method, url, true);
      if (headers && headers.length > 0) {
        headers.forEach((header) => {
          for (const prop in header) {
            xmlhttp.setRequestHeader(prop, header[prop]);
          }
        });
      }
      if (method !== 'POST') {
        xmlhttp.send(buildQueryString(data));
      } else if (method === 'POST' && contactUsForms) {
        xmlhttp.send(buildQueryString(data));
      } else {
        xmlhttp.send(data);
      }
    }
  );
}

/**
 * A jsonp wrapper for the requests on API's that will not change their Access Headers.
 * Setting fetch headers to no-cors would result in a opaque response that the UI would never be able to access.
 * See: https://developers.google.com/web/updates/2015/03/introduction-to-fetch?hl=en
 * @param url
 * @param options - { callbackName [String], callback [Function] }
 */
function jsonp(url, options) {
  const script = document.createElement('script');
  script.type = 'text/javascript';
  script.async = true;
  script.src = url;
  window[options.callbackName] = (data) => {
    options.callback(data);
  };
  document.getElementsByTagName('head')[0].appendChild(script);
}

/**
 * Get query string as an object. Same output as redux-routers `location` object.
 */
function location() {
  const obj = {
    storefront: '',
    route: '',
    third: '',
    query: {},
  };

  const queryItems = window.location.search.replace('?', '').split('&');
  for (const item of queryItems) {
    const string = item?.split('=');
    obj.query[string[0]] = string[1];
  }

  const path = window.location.pathname.split('/');
  obj.storefront = path[1].split('-')[1];
  obj.route = path[2];
  obj.third = path[3];
  return obj;
}

// ************* Inicio de bloque para Profile ******************

function retriveActualHostUrl(host = 'aeromexico.com', language = 'es', storefront = 'es-mx') {
  const profile = {
    en: 'profile',
    es: 'perfil',
    fr: 'profile',
    jp: 'profile',
    pt: 'perfil',
    it: 'profilo',
  };

  const path = `/${storefront}/${profile[language]}`;

  if (typeof host === 'string' && host.includes('localhost')) {
    window.location.assign(`http://${host + path}`);
  } else {
    window.location.assign(`https://${host + path}`);
  }
}

function retriveActualHostUrEFourRegister(host = 'https://aeromexico.com') { // tiene que cambiar por una redireccion.
  const profile = '/es-mx/registrate/';
  if (typeof host === 'string' && host.includes('localhost')) {
    window.location.replace(`http://${host}:3000${profile}`);
  } else {
    window.location.replace(`https://${host}${profile}`);
  }
}

function redirectMyTrips(host = 'https://aeromexico.com', path, storefront, isLogged = false, lastName, pnr) {
  if (host.includes('localhost')) {
    host = `${host}:3000`;
  }

  const queryParams = `last-name=${lastName}&pnr=${pnr}`;
  if (isLogged) {
    window.location.replace(`http://${host}/${storefront}/perfil?${queryParams}`);
  } else {
    window.location.replace(`http://${host}/${storefront}/${path}/trips?${queryParams}`);
  }
}

function redirectExchanges(path, lastName, pnr, parameters = '') {
  const host = window.location.hostname;
  const isProduction = host.includes('aeromexico.com');
  const hasTripleW = host.includes('www');
  const isAlpha = host.includes('alpha');
  const url = `${isProduction && !hasTripleW && !isAlpha ? 'https://www.' : 'https://'}${host}${path}${lastName}&pnr=${pnr}${parameters ? `&${parameters}` : ''}`;
  window.location.href = url;
}

function redirectToHome(storefront, path, host = 'https://aeromexico.com') {
  return path ? `${host}/${storefront}/${path}?` : `${host}/${storefront}`;
}

const getInitials = (initials = 'AA') => {
  if (initials)
    return initials.substring(0, 1).toUpperCase();
  else
    return 'TE';
};

const getUserFirstName = (firstName) => {
  if (firstName)
    return firstName;
  else
    return 'Test';
};

const formatUserSessionCookie = (data = {}, token) => {
  if (Object.values(data).length === 0) return;
  const expires = new Date();
  expires.setTime(expires.getTime() + (30 * 60 * 1000));
  const userSessionCookie = {
    firstName: data.userProfile.passengerData.firstName,
    lastName: data.userProfile.passengerData.lastName,
    userToken: (data.userToken) ? data.userToken : token,
    accountNumber: String(data.userProfile.ffNumber).padStart(11, '0'),
    ffNumber: data.userProfile.ffNumber,
    timeExpiration: Moment(expires).utc().format('HHmm.ss'),
    isMicroToken: false,
  };

  setCookie('userSession', serialize(userSessionCookie, { isJson: true }), null, expires);
};

// ************* Fin de bloque para Profile ******************


function getCurrencyCodeByStorefront(storefront) {
  switch (storefront) {
    case 'us':
    case 'gt':
    case 'cr':
    case 'pe':
    case 'ec':
    case 'cl':
    case 'do':
    case 'hn':
    case 'pa':
    case 'ni':
    case 'sv':
      return 'USD';
    case 'mx':
      return 'MXN';
    case 'nl':
    case 'es':
    case 'eu':
    case 'fr':
      return 'EUR';
    case 'gb':
      return 'GBP';
    case 'br':
      return 'BRL';
    case 'ca':
      return 'CAD';
    default:
      return 'USD';
  }
}

function getElementOffset(element) {
  let top = 0;
  let left = 0;

  while (element) {
    top += element.offsetTop || 0;
    left += element.offsetLeft || 0;
    element = element.offsetParent;
  }

  return {
    top: top,
    left: left,
  };
}

function getCookie(key) {
  const cookie = `; ${document.cookie}`;
  const value = cookie.split(`; ${key}=`);

  if (value.length === 2) {
    return value.pop().split(';').shift();
  }

  return null;
}

function getHostFromCMS() {
  const location = window.location.host;
  const isLocalHost = location.search('localhost');
  let host = '';
  if (isLocalHost > 0) {
    host = getHostCMS().replace('/cms/api/v1', '').replace('/myb', '');
    host = host + ':' + window.location.port;
  } else {
    host = getHostCMS(location).replace('/cms/api/v1', '').replace('/myb', '');
  }
  return host;
}

function setCookie(key, val, expirationDays, expirationDate) {
  let expires = '';
  if (expirationDays) {
    const date = new Date();
    date.setTime(date.getTime() + (expirationDays * 24 * 60 * 60 * 1000));
    expires = `; expires="${date.toGMTString()}`;
  } else if (expirationDate) {
    expires = `; expires="${expirationDate.toGMTString()}`;
  }
  document.cookie = `${key}=${val}${expires};path=/`;
}

function deleteCookie(key) {
  const now = new Date();
  document.cookie = `${key}=ok; expires=${now.toGMTString()}; path=/`;
}

function isCheckInFromLocation() {
  const route = location().route;
  let isCheckIn = false;
  if (route === 'check-in' || route === ' enregistrement') {
    isCheckIn = true;
  }
  return isCheckIn;
}

/**
 * Add Moment Round Functions
 * See: https://github.com/WebDevTmas/moment-round
 * @param {object} momentObject
 * @param {number} precision
 * @returns {object} momentObject
 */
function momentRound(momentObject, precision, key, direction = 'round') {
  const keys = ['Hours', 'Minutes', 'Seconds', 'Milliseconds'];
  const maxValues = [24, 60, 60, 1000];

  // Capitalize first letter
  key = key.charAt(0).toUpperCase() + key.slice(1).toLowerCase();

  // make sure key is plural
  if (key.indexOf('s', key.length - 1) === -1) {
    key += 's';
  }
  let value = 0;
  let rounded = false;
  let subRatio = 1;
  let maxValue = '';
  for (const i in keys) {
    const k = keys[i];
    if (k === key) {
      value = momentObject._d['get' + key]();
      maxValue = maxValues[i];
      rounded = true;
    } else if (rounded) {
      subRatio *= maxValues[i];
      value += momentObject._d['get' + k]() / subRatio;
      momentObject._d['set' + k](0);
    }
  }

  value = Math[direction](value / precision) * precision;
  value = Math.min(value, maxValue);
  momentObject._d['set' + key](value);

  return momentObject;
}

function getUpgradeAvailable(cartPNRModel, legIndex) {
  const upsellOffer = get(cartPNRModel, `upsellOffers[${legIndex}]`, {});
  const isUpgradeAvailable = upsellOffer.upgradeAvailable;
  const firstAncillary = get(cartPNRModel, 'travelerCollection[0].ancillaries.collection[0]', { _meta: {} });
  const isBought = firstAncillary._meta.class === 'PaidTravelerAncillary' && firstAncillary.type === 'UPG';
  return !isBought && isUpgradeAvailable;
}

const CARRIER_BRANDS = {
  AM: 'Aeromexico',
  DL: 'Delta',
};
export const carrierToBrand = (carrier = 'AM') => (CARRIER_BRANDS[carrier] || 'Aeromexico');
const carrierToBrandCode = (carrier = 'AM') => (carrier === '5D' ? 'AM' : carrier);

export const createSegmentFilter = action => s => s.segment.segmentCode === action.segmentCode;

function isHomeLocation(language, storefront) {
  const isHomeLocation = window.location.pathname === `/${language}-${storefront}` || window.location.pathname == `/${language}-${storefront}/`;

  let isHome = false;
  if (isHomeLocation) {
    isHome = true;
  }
  return isHome;
}

const getCobrandCardsUser = (isSignedIn, userProfile) => {
  let userCards = null;

  if (isSignedIn && size(get(userProfile, 'account.cobrandCardCollection._collection'))) {
    userCards = get(userProfile, 'account.cobrandCardCollection._collection');
  }

  return userCards;
};

const getNameLastNameSplit = (firstName, lastName) => {
  return (firstName?.split(' ')?.[0] + ' ' + lastName?.split(' ')?.[0]).toUpperCase();
};

// localStorage API
const localStorageName = keyMirror({
  persistentLogin: null,
  tabIndex: null,
  userSession: null,
});

// tabIndex se usa para los redirects de profile que se hacen a e4
const setTabIndex = (tabIndex) => {
  safeLocalStorage.setItem(localStorageName.tabIndex, tabIndex);
};

const removeTabIndex = () => {
  safeLocalStorage.removeItem(localStorageName.tabIndex);
};

const getTabIndex = () => {
  return safeLocalStorage.getItem(localStorageName.tabIndex);
};

const isLoginDataInStorage = () => {
  const persistentLogin = safeLocalStorage.getItem(localStorageName.persistentLogin);
  const userSession = getCookie(localStorageName.userSession);

  return Boolean(persistentLogin || userSession);
};

const deleteSpacesText = (text) => {
  const isText = typeof text === 'string';
  return isText ? text.replace(/[\s]/g, '') : text;
};

const getPos = (storeFront = 'mx', language = 'es') => ({
  'store': storeFront,
  'pos': 'WEB',
  'language': language,
});

const getHoursToExpireFareLock = (creationDate, hoursToExpire = 0, storefront = '') => {
  if (creationDate) {
    const language = storefront?.split('-')?.[0];
    Moment.locale(language);
    const date = Moment();
    const creationDateSF = Moment(creationDate).locale(storefront);

    const returnedEndate = creationDateSF.add(hoursToExpire, 'hours');

    date.subtract(6, 'hours');
    const hoursToExpireFareLock = returnedEndate.diff(date, 'hours');
    const minutesToExpireFareLock = returnedEndate.diff(date, 'minutes');
    const isExpired = minutesToExpireFareLock < 0;

    return {
      hoursToExpireFareLock,
      isExpired,
      returnedEndate,
    };
  } else {
    return {
      hoursToExpireFareLock: null,
      isExpired: true,
      returnedEndate: null,
    };
  }
};

const getTravelerAgeInMonths = (dateOfBirth) => {
  const age = Moment().diff(Moment(dateOfBirth, 'YYYY-MM-DD'), 'months');

  return age >= 24 && age <= 1091;
};


const getPageReferrer = (url) => {
  let result = 'NA';
  if (url) {
    const newUrl = new URL(url);
    if (newUrl.pathname.length > 1) {
      result = newUrl.pathname;
    }
  }
  return result;
};

const SF_LANG_MAP = {
  'es-mx': 'ES',
  'es-us': 'ES',
  'en-us': 'EN',
  'pt-br': 'PT-BR',
  'fr-ca': 'FR-CA',
  'en-ca': 'EN',
};

const SF_PATTERN = new RegExp(Object.keys(SF_LANG_MAP).join('|'), 'i');

function updateSFLangFromURL(url) {
  const match = url.match(SF_PATTERN);
  if (match) {
    const key = match[0].toLowerCase();
    const lang = SF_LANG_MAP[key];
    if (window.SF_LANG !== lang) {
      window.SF_LANG = lang;
    }
  } else {
    window.SF_LANG = undefined; // Esta parte aun esta por definir el default
  }
}

function getAncillaryNameInsurance(insurance) {
  let newName = null;
  switch (insurance) {
    case 'AMS': newName = 'Basic Travel Insurance'; break;
    case 'SEP': newName = 'Luggage Insurance PLUS'; break;
    case 'SDE': newName = 'Luggage Insurance'; break;
    case 'SXC': newName = 'Cancellation Insurance'; break;
    case 'SXP': newName = 'Cancellation Insurance PLUS'; break;
    case 'SVI': newName = 'International Travel Insurance'; break;
    default: newName = null;
  }

  return newName;
}

const returnFfnLeadingZeros = (ffn) => {
  return `${ffn}`.padStart(11, '0');
};

const SEAT_CHOICES = {
  EXIT_ROW: 'Emergency seats',
  PREFERRED: 'Preferred seats',
  COACH: 'Seat choice (Tourist Seat)',
  AM_PLUS: 'AM Plus seats',
  FIRST_CLASS: 'Premier seats',
  FIRST_ONE_CLASS: 'Premier One seats',
  FIRST_LIGHT_CLASS: 'Premier Light seats',
};

function getFareType(segment) {
  const fareCabin = segment?.fareAndCabinName || segment?.fareFamily;
  let fare = '';
  switch (fareCabin) {
    case 'CLASSIC_FARE_TOURIST_CABIN':
      fare = 'CLASSIC';
      break;
    case 'CLASSIC_FLEXIBLE_FARE_TOURIST_CABIN':
      fare = 'CLASSIC FLEX';
      break;
    case 'AMPLUS_FARE_TOURIST_CABIN':
      fare = 'AM+';
      break;
    case 'AMPLUS_FLEXIBLE_FARE_TOURIST_CABIN':
      fare = 'AM+ FLEX';
      break;
    case 'PREMIER_FARE_PREMIER_CABIN':
      fare = segment.isPremierOne ? 'PREMIER ONE' :
        segment.isPremierLight ? 'PREMIER LIGHT' : 'PREMIER';
      break;
    case 'PREMIER_FLEXIBLE_FARE_PREMIER_CABIN':
      fare = 'PREMIER FLEX';
      break;
    case 'PREMIER_ONE_FLEX_FARE_PREMIER_CABIN':
    case 'PREMIER_ONE_FLEXIBLE_FARE_PREMIER_CABIN':
      fare = 'PREMIER ONE FLEX';
      break;
    case 'PREMIER_LIGHT_FARE_PREMIER_CABIN':
      fare = 'PREMIER LIGHT';
      break;
    case 'PREMIER_LIGHT_FLEXIBLE_FARE_PREMIER_CABIN':
      fare = 'PREMIER LIGHT FLEX';
      break;
    case 'CLASSIC_CORPORATE_FLEX_FARE_TOURIST_CABIN':
      fare = 'CORPORATE CLASSIC FLEX';
      break;
    case 'CLASSIC_CORPORATE_FARE_TOURIST_CABIN':
      fare = 'CORPORATE CLASSIC';
      break;
    default:
      fare = segment?.fareFamily || 'NA';
      break;
  }

  return fare;
}

function getDtd(depDate) {
  const formatedDepDate = Moment.isMoment(depDate) ? depDate.format('YYYY-MM-DDTHH:mm:ss') : depDate;
  const nd = new Date();
  const dd = new Date(formatedDepDate);
  const diff = Math.abs(nd - dd);
  const dias = Math.floor(diff / (1000 * 60 * 60 * 24));
  const horas = Math.floor((diff - (dias * 1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const min = Math.floor((diff - (dias * 1000 * 60 * 60 * 24) - (horas * 1000 * 60 * 60)) / (1000 * 60));
  const dtd = dias + '/' + horas.toString().padStart(2, '0') + ':' + min.toString().padStart(2, '0');

  return dtd;
}

const insuranceObject = {
  'AMS': true,
  'SEP': true,
  'SDE': true,
  'SXC': true,
  'SXP': true,
  'SVI': true,
};

function getAncillariesSelected(travelerCollection, products) {
  const ancillaryMap = {};
  let typeOfProductView = products?.[0]?.id || products?.products?.[0]?.id || products?.ancillary?.item?.id;
  typeOfProductView = typeOfProductView === 'CBX' ? '029' :
    products?.seat ? 'seats' :
      insuranceObject[typeOfProductView] ? 'insurance' : typeOfProductView;
  (travelerCollection || []).forEach(traveler => {
    const ancillariesPerTravelerSelected = typeOfProductView === 'seats' ? (traveler?.segmentChoices?.items || []).filter(ancillary => !ancillary?.isPaid) :
      (traveler?.ancillaries?.ancillaries || []).filter(ancillary => !ancillary?.isPaid);
    (ancillariesPerTravelerSelected || []).forEach((ancillary) => {
      switch (typeOfProductView) {
        case 'seats':
          const key = `${ancillary?.segmentCode}-${ancillary?.seat?.code}-${ancillary?.seat?.currency?.total}`;
          if (!ancillaryMap[key]) {
            ancillaryMap[key] = {
              quantity: 0,
              redeemedCP: ancillary?.redeemedCP,
              segmentCode: ancillary?.segmentCode,
              type: ancillary?.seat?.code,
              price: ancillary?.seat?.currency?.total,
              pricepoints: ancillary?.seat?.currency?.totalCPPoints || 0,
            };
          }
          ancillaryMap[key].quantity += 1;
          break;
        case 'insurance':
          if (insuranceObject[ancillary?.ancillary?.type]) {
            const key = `${ancillary?.ancillary?.segmentCode || ancillary?.legCode}-${ancillary?.ancillary?.type}-${ancillary?.ancillary?.ancillaryOffer?.ancillary?.currency?.total || ancillary?.ancillary?.currency?.total}`;
            if (!ancillaryMap[key]) {
              ancillaryMap[key] = {
                quantity: 0,
                redeemedCP: ancillary?.ancillary?.redeemedCP,
                segmentCode: ancillary?.ancillary?.segmentCode,
                type: ancillary?.ancillary?.type,
                price: ancillary?.ancillary?.ancillaryOffer?.ancillary?.currency?.total || ancillary?.ancillary?.currency?.total,
                pricepoints: 0,
              };
            }
            ancillaryMap[key].quantity += 1;
          }
        default:
          if (ancillary?.ancillary?.type === typeOfProductView) {
            const key = `${ancillary?.ancillary?.segmentCode || ancillary?.legCode}-${ancillary?.ancillary?.type}-${ancillary?.ancillary?.ancillaryOffer?.ancillary?.currency?.total || ancillary?.ancillary?.currency?.total}`;

            if (!ancillaryMap[key]) {
              ancillaryMap[key] = {
                quantity: 0,
                redeemedCP: ancillary?.ancillary?.redeemedCP,
                segmentCode: ancillary?.ancillary?.segmentCode,
                type: ancillary?.ancillary?.type,
                price: ancillary?.ancillary?.ancillaryOffer?.ancillary?.currency?.total || ancillary?.ancillary?.currency?.total,
                pricepoints: typeOfProductView === 'UPG' ? ancillary?.ancillary?.ancillaryOffer?.ancillary?.currency?.totalCPPoints : 0,
              };
            }
            ancillaryMap[key].quantity += 1;
          }
          break;
      }
    });
  });

  const ancillariesToView = Object.values(ancillaryMap);
  return [ancillariesToView, typeOfProductView];
}

function getRegionValue(marketType, regionCode, legRegion) {
  const regionCodeMap = {
    'CAN': 'CAN',
    'USA': 'USA',
  };
  const regionMap = {
    'ASI': 'ASI',
    'ASIA': 'ASI',
    'CANADA': 'CAN',
    'CENTROAMERICA': 'CAM',
    'CENTROAMERICA_CARIBE': 'CAR',
    'DOMESTIC': 'DOM',
    'EUROPA': 'EUR',
    'Frontera': 'FRO',
    'Local': 'LOC',
    'Playa': 'PLA',
    'SUDAMERICA_ASIA': 'SUD',
    'SUDSH': 'SUD',
    'SUDLH': 'SUD',
    'CAN': 'CAN',
    'CAM': 'CAM',
    'CAR': 'CAR',
    'DOM': 'DOM',
    'EUR': 'EUR',
    'FRO': 'FRO',
    'LOC': 'LOC',
    'PLA': 'PLA',
    'SUD': 'SUD',
  };

  if (marketType === 'TRANSBORDER') {
    return regionCodeMap[regionCode] || 'DOM';
  } else {
    return regionMap[legRegion] || 'DOM';
  }
}

function getOriginAndDestinarionAirports(pnrInfo) {
  const legCollection = pnrInfo?.legCollection;
  let destination = null;
  let origin = null;

  if (pnrInfo?.flightItineraryType === 'ROUNDTRIP' || pnrInfo?.flightItineraryType === 'ONEWAY') {
    origin = legCollection?.[0]?.segmentsCollection?.[0]?.departureAirport || 'NA';
    destination = legCollection?.[0]?.segmentsCollection?.slice(-1)?.[0]?.arrivalAirport || 'NA';
  } else {
    origin = legCollection?.map(legs => {
      const segmentCollection = legs?.segmentsCollection || [];
      const segmentOrigin = segmentCollection?.[0]?.departureAirport || 'NA';
      return segmentOrigin;
    }).join('-');
    destination = legCollection?.map(legs => {
      const segmentCollection = legs?.segmentsCollection || [];
      const segmentDestination = segmentCollection?.slice(-1)?.[0]?.arrivalAirport || 'NA';
      return segmentDestination;
    }).join('-');
  }

  return { origin, destination };
}

function hasAnyBaggageToRedeem(dataBaggage) {
  let hasAncillary = false;
  Object.keys(dataBaggage).forEach(legCode => {
    Object.keys(dataBaggage[legCode]).forEach(traverlerID => {
      const ancillaryCollection = dataBaggage[legCode][traverlerID]?.updateAncillaryOfferCollection;
      ancillaryCollection.forEach(ancillary => {
        if (ancillary?.redeemed && !ancillary?.isPaid) {
          hasAncillary = true;
        }
      });
    });
  });

  return hasAncillary;
}

function getBeneficiaryTierIndex(passengersBeneficiaryTiers, pnrModel) {
  let index = 0;

  const passengers = pnrModel.getAllPassengers();
  Object.keys(passengers).forEach(key => {
    Object.keys(passengersBeneficiaryTiers).forEach(i => {
      if (passengersBeneficiaryTiers?.[i]?.id == passengers?.[key]?.id) {
        index = index + 1;
        passengersBeneficiaryTiers[i].realIndex = index;
        passengersBeneficiaryTiers[i].firstName = passengers?.[key]?.firstName;
      }
    });
  });

  return passengersBeneficiaryTiers;
}

function getAllTierBeneficiary(pnrModel) {
  const passengersWithFriendsAndFamilyBenefits = (pnrModel?.remarks || []).filter(remark => remark.startsWith('FF'));

  let passengersBeneficiaryTiers = passengersWithFriendsAndFamilyBenefits && passengersWithFriendsAndFamilyBenefits.map((str) => {
    const segments = str?.split('/');
    const id = segments[segments.length - 1];

    return { id, tier: segments[1] === 'TOPTIER' ? segments[2] : segments[1], isToptier: segments[1] === 'TOPTIER' ? true : false };
  });

  passengersBeneficiaryTiers = getBeneficiaryTierIndex(passengersBeneficiaryTiers, pnrModel);

  return passengersBeneficiaryTiers || [];
}

function getBeneficiaryTierById(pnrModel, travelerId) {
  const passengersBeneficiaryTiers = getAllTierBeneficiary(pnrModel);
  const beneficiaryTier = passengersBeneficiaryTiers?.find(benefit => benefit?.id === travelerId);

  return beneficiaryTier;
}


function getTopTierBeneficiary(pnrModel) {
  const passengersBeneficiaryTiers = getAllTierBeneficiary(pnrModel);
  const beneficiaryTopTier = passengersBeneficiaryTiers.find(benefit => benefit?.isToptier);

  return beneficiaryTopTier;
}

function getFFDataToRedeem(legCode, travelerID, roundtripSwitch, benefitsCore, pnrModel, switchState) {
  let travelers = pnrModel?.cart?.travelerCollection;
  const traveler = travelers.find((traveler) => traveler?.id === travelerID);
  const isOW = pnrModel?.flightItineraryType == 'ONEWAY';
  let benefit = null;
  let isCobrand = null;
  let cobrandCode = null;
  let isTier = null;
  let tierCode = null;
  let isFriendAndFamilyBenefit = null;
  let ffamilyCode = null;
  let datasToRedeemed = null;
  let travelersIds = [];

  const allLegs = isOW || roundtripSwitch;
  const legCodes = allLegs ? pnrModel.getLegCodesForAllLegs() : [legCode];

  if (switchState || roundtripSwitch) {
    datasToRedeemed = [];
    travelersIds = [];
    travelers = !switchState ? [traveler] : travelers;

    legCodes.forEach(legCode => {
      Object.keys(travelers).forEach(key => {
        const hasFrequentFlyerNumber = travelers[key]?.frequentFlyerNumber;
        const beneficiaryTier = getBeneficiaryTierById(pnrModel, travelers?.[key]?.id);
        const anyBaggagePaidBeforeRedeem = travelers[key]?.ancillaries?.ancillaries?.some(ancillary => ancillary?.groupCode == 'BG' && ancillary?.isPaid && !ancillary?.redeemed);

        if ((hasFrequentFlyerNumber || beneficiaryTier) && !anyBaggagePaidBeforeRedeem) {
          const fullAccountNumber = String(travelers[key]?.frequentFlyerNumber).padStart(11, '0');
          benefit = benefitsCore?.find(benefit => (benefit?.accountNumber == fullAccountNumber) && (benefit?.details?.split('|')?.[0] == legCode));

          if (!benefit) {
            benefit = benefitsCore?.find(benefit => (benefit?.accountNumber == travelers[key]?.frequentFlyerNumber) && (benefit?.details?.split('|')?.[0] == legCode));
          }

          if (!benefit && beneficiaryTier && !beneficiaryTier.isToptier) {
            benefit = benefitsCore?.find(benefit => {
              const detailSplitted = benefit?.details?.split('|');
              return (detailSplitted?.[detailSplitted?.length - 1] == travelers?.[key]?.id) && (detailSplitted?.[0] == legCode);
            });
          }

          isCobrand = benefit?.cobrandBenefits?.baggage && benefit?.cobrandCode;
          cobrandCode = isCobrand ? benefit?.cobrandCode : '';

          isTier = benefit?.tierBenefits?.baggage && benefit?.tier;
          tierCode = isTier ? benefit?.tier : '';

          const matchesTiers = {
            'ELP': 'ELITEPLUS',
            'ELT': 'ELITE',
            'CA': 'CABALLEROAGUILA',
          };

          tierCode = matchesTiers[tierCode] || tierCode;

          isFriendAndFamilyBenefit = benefit?.['f&fBenefits']?.baggage;
          ffamilyCode = isFriendAndFamilyBenefit ? benefit?.tier : null;

          const cardName = cobrandCode?.includes('AMX') ? 'AMERICAN EXPRESS' : cobrandCode?.includes('SE') ? 'SANTANDER' : null;
          const hasBenefit = isCobrand || isFriendAndFamilyBenefit || isTier;
          const benefitCode = isCobrand ? cobrandCode : isTier ? tierCode : isFriendAndFamilyBenefit ? ffamilyCode : '';

          if (hasBenefit) {
            travelersIds.push(travelers[key]?.id);
            const data = {
              isRedeemed: true,
              id: travelers[key]?.id,
              premierLevel: tierCode,
              cobrandCode: benefitCode,
              cardName: cardName,
              legCode,
            };

            if (beneficiaryTier && !beneficiaryTier?.isToptier && !isCobrand) {
              datasToRedeemed.push({ ...data, beneficiaryTier: beneficiaryTier?.tier, isRedemptionBeneficiaryTier: true });
            } else {
              datasToRedeemed.push(data);
            }
          }
        }
      });
    });
  } else {
    const hasFrequentFlyerNumber = traveler?.frequentFlyerNumber;
    const beneficiaryTier = getBeneficiaryTierById(pnrModel, traveler?.id);
    const anyBaggagePaidBeforeRedeem = traveler?.ancillaries?.ancillaries?.some(ancillary => ancillary?.groupCode == 'BG' && ancillary?.isPaid && !ancillary?.redeemed);

    if ((hasFrequentFlyerNumber || beneficiaryTier) && !anyBaggagePaidBeforeRedeem) {
      const fullAccountNumber = String(traveler?.frequentFlyerNumber).padStart(11, '0');
      benefit = benefitsCore?.find(benefit => (benefit?.accountNumber == fullAccountNumber) && (benefit?.details?.split('|')?.[0] == legCode));

      if (!benefit) {
        benefit = benefitsCore?.find(benefit => (benefit?.accountNumber == traveler?.frequentFlyerNumber) && (benefit?.details?.split('|')?.[0] == legCode));
      }

      if (!benefit && beneficiaryTier && !beneficiaryTier.isToptier) {
        benefit = benefitsCore?.find(benefit => {
          const detailSplitted = benefit?.details?.split('|');
          return (detailSplitted?.[detailSplitted?.length - 1] == travelerID) && (detailSplitted?.[0] == legCode);
        });
      }

      isCobrand = benefit?.cobrandBenefits?.baggage && benefit?.cobrandCode;
      cobrandCode = isCobrand ? benefit?.cobrandCode : '';

      isTier = benefit?.tierBenefits?.baggage && benefit?.tier;
      tierCode = isTier ? benefit?.tier : '';

      const matchesTiers = {
        'ELP': 'ELITEPLUS',
        'ELT': 'ELITE',
        'CA': 'CABALLEROAGUILA',
      };

      tierCode = matchesTiers[tierCode] || tierCode;

      isFriendAndFamilyBenefit = benefit?.['f&fBenefits']?.baggage;
      ffamilyCode = isFriendAndFamilyBenefit ? benefit?.tier : null;
    }

    const cardName = cobrandCode?.includes('AMX') ? 'AMERICAN EXPRESS' : cobrandCode?.includes('SE') ? 'SANTANDER' : null;
    const hasBenefit = isCobrand || isFriendAndFamilyBenefit || isTier;
    const benefitCode = isCobrand ? cobrandCode : isTier ? tierCode : isFriendAndFamilyBenefit ? ffamilyCode : '';

    if (hasBenefit) {
      travelersIds.push(traveler?.id);
      const data = {
        id: traveler?.id,
        isRedeemed: true,
        premierLevel: tierCode,
        cobrandCode: benefitCode,
        cardName: cardName,
        legCode,
      };

      if (beneficiaryTier && !beneficiaryTier?.isToptier && !isCobrand) {
        datasToRedeemed = { ...data, beneficiaryTier: beneficiaryTier?.tier, isRedemptionBeneficiaryTier: true };
      } else {
        datasToRedeemed = { ...data };
      }
    }
  }

  return { datasToRedeemed, travelersIds };
}


const getEnviroment = () => {
  const env = location.hostname;
  const isProd = /^(www.)*(aeromexico)\.com$/gi.test(env);
  return isProd;
};

function minutesToTargetDate(date) {
  const targetDate = new Date(date);
  const now = new Date();
  const differenceMs = targetDate.getTime() - now.getTime();

  if (differenceMs < 0) {
    return false; // Or return 0, or handle differently
  }

  const minutes = Math.floor(differenceMs / (1000 * 60));
  return minutes;
}

function getDaysToDepature(depDate) {
  const formatedDepDate = Moment.isMoment(depDate) ? depDate.format('YYYY-MM-DDTHH:mm:ss') : depDate;
  const nd = new Date();
  const dd = new Date(formatedDepDate);
  const diff = Math.abs(nd - dd);
  const dias = Math.floor(diff / (1000 * 60 * 60 * 24));
  const horas = Math.floor((diff - (dias * 1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const min = Math.floor((diff - (dias * 1000 * 60 * 60 * 24) - (horas * 1000 * 60 * 60)) / (1000 * 60));
  const dtd = dias + 'd ' + horas.toString().padStart(2, '0') + 'h ' + min.toString().padStart(2, '0') + 'm';

  return dtd;
}

/**
 * Determines if carry-on 0CZ should be shown first for basic light fare
 * Based on SSM-8025 requirements
 * @param {Object} pnrModel - PNR model containing flight and traveler information
 * @returns {boolean} - true if carry-on should be shown first
 */
function shouldShowCarryOnFirstForBasicLight(pnrModel) {
  if (!pnrModel) return false;
  // Check if domestic flight
  if (pnrModel.globalMarketFlight !== 'DOMESTIC') return false;
  // Check if basic light fare
  if (!pnrModel.isBasicLightFare()) return false;
  // Check if any traveler already bought 0CZ
  if (pnrModel.hasAlreadyPurchasedAncillary('0CZ')) return false;
  return true;
}

/**
 * Determines if the taveller has selected the 0CZ carry-on.
 * @param {Object} traveler
 * @returns {boolean}
 */
function hasSelected0CZCarryOn(traveler) {
  const ancillaryOffer = traveler?.updateAncillaryOfferCollection;
  if (ancillaryOffer?.length) {
    const found = ancillaryOffer.find(ancillary => ancillary.type === '0CZ');
    return !!found;
  }
  return false;
}

/**
 * Determines if the taveller has bought the 0CZ carry-on.
 * @param {Object} travelerAncillary
 * @returns {boolean}
 */
function hasBought0CZCarryOn(travelerAncillary) {
  return travelerAncillary?.paidCarryOn && travelerAncillary?.code === '0CZ';
}

function getTripSFRedirect(storefront) {
  const tripSFRedirect = {
    en: '/manage-your-booking',
    es: '/administra-tu-viaje',
    fr: '/gerez-votre-reservation',
    pt: '/gerencie-sua-reserva',
    it: '/gestisci-viaggio',
    jp: '/manage-your-booking',
  };

  const lang = storefront && typeof storefront === 'string' ? storefront.split('-')[0] : null;

  return lang && tripSFRedirect[lang] ? tripSFRedirect[lang] : tripSFRedirect.en;
}

export {
  bagNames,
  buildQueryString,
  carrierToBrandCode,
  deleteCookie,
  deleteSpacesText,
  emptyBag,
  formatUserSessionCookie,
  luggageAncillary,
  getAllTierBeneficiary,
  getAncillaryNameInsurance,
  getAncillariesSelected,
  getBookingClassForBoletoPremio,
  getBoxImageFarePremierLocalURL,
  getCabinImageURL,
  getCookie,
  getCurrencyCodeByStorefront,
  getDaysToDepature,
  getDefaultOption,
  getDefaultOptionAirport,
  getDtd,
  getElementOffset,
  getEnviroment,
  getFareAndCabin,
  getFareAndCabinName,
  getFareFamilyName,
  getFareType,
  getFFDataToRedeem,
  getFlightTypeCopyIDByLeg,
  getFormattedNumber,
  getFormattedNumberNoCountryCode,
  getHighestCabinAircraft,
  getHostFromCMS,
  getHoursToExpireFareLock,
  getInitials,
  getBeneficiaryTierById,
  getTopTierBeneficiary,
  getCobrandCardsUser,
  getNameLastNameSplit,
  getNumberOfDaysCopyID,
  getOriginAndDestinarionAirports,
  getPos,
  getRandomKey,
  getRegionValue,
  getSegmentCodesUpsellOffers,
  getStorefrontDropdownOptions,
  getStorefrontDropdownOptionsExcept,
  getStorefrontIntertitialValues,
  getStorefrontSeatMapDiscountValues,
  getTabIndex,
  getTimeFormat,
  getTimeFormatCopyID,
  getTravelerAgeInMonths,
  getTripSFRedirect,
  getUpgradeAvailable,
  getUserFirstName,
  hasSomeSeatSelectedBySegment,
  hasAnyBaggageToRedeem,
  hasBought0CZCarryOn,
  hasSelected0CZCarryOn,
  isCheckInFromLocation,
  isHomeLocation,
  isIrops,
  isJsonObject,
  isLoginDataInStorage,
  isPnrOrEticket,
  isSameArrays,
  isSameObjects,
  isValidAgeForInsurance,
  jsonp,
  location,
  momentRound,
  minutesToTargetDate,
  openCurrentWebsiteManageBooking,
  openUrlInNewTab,
  openUrlInNewWindow,
  passengerCount,
  preHTML,
  redirectMyTrips,
  redirectExchanges,
  redirectToHome,
  removeTabIndex,
  retriveActualHostUrEFourRegister,
  retriveActualHostUrl,
  returnFfnLeadingZeros,
  sendHttpRequest,
  SEAT_CHOICES,
  setCookie,
  setTabIndex,
  shouldShowCarryOnFirstForBasicLight,
  sortArrayByProperty,
  sortAlphabetically,
  upgradeAncillary,
  getPageReferrer,
  SF_LANG_MAP,
  updateSFLangFromURL,
};
